import * as React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";
import { isNotificationsCentrePathname } from "../../constants/notificationsCentre";
import { usePageContentReady } from "../../hooks/usePageContentReady";
import { LoadingChart } from "./loading-chart";
import "./loading-chart.css";

function parseUniversityCourseId(pathname: string): string | null {
  const match = pathname.match(/^\/university\/course\/([^/]+)(?:\/|$)/);
  return match?.[1] ?? null;
}

/** Skip the global route loader when moving within the same University course. */
function shouldSkipUniversityCourseLoader(
  previousPath: string,
  nextPath: string
): boolean {
  const previousCourseId = parseUniversityCourseId(previousPath);
  const nextCourseId = parseUniversityCourseId(nextPath);
  if (!previousCourseId || !nextCourseId || previousCourseId !== nextCourseId) {
    return false;
  }

  const isUniversityCourseFlow =
    /^\/university\/course\/[^/]+(?:\/module\/[^/]+\/lesson\/[^/]+)?$/.test(previousPath) &&
    /^\/university\/course\/[^/]+(?:\/module\/[^/]+\/lesson\/[^/]+)?$/.test(nextPath);

  return isUniversityCourseFlow;
}

/**
 * Skip starting a new global route loader for in-inbox notification selection /
 * category switches — detail swaps in place. Navigating into the centre from
 * elsewhere still starts the loader; a follow-up replace to a notification id
 * must not abort that run (see RouteChangeLoader).
 */
function shouldSkipNotificationsCentreLoader(
  previousPath: string,
  nextPath: string
): boolean {
  return (
    isNotificationsCentrePathname(previousPath) &&
    isNotificationsCentrePathname(nextPath)
  );
}

function isAuthPathname(pathname: string): boolean {
  return pathname === "/auth" || pathname.startsWith("/auth/");
}

function isDemoEntryAnalysisPathname(pathname: string): boolean {
  return (
    pathname === "/demo/entry-analysis" || pathname.startsWith("/demo/entry-analysis/")
  );
}

/**
 * Skip the global route loader between auth screens — sign-in and sign-up
 * criss-cross their hero/form columns, and the overlay would hide it.
 */
function shouldSkipAuthLoader(previousPath: string, nextPath: string): boolean {
  return isAuthPathname(previousPath) && isAuthPathname(nextPath);
}

/** Demo funnel has no dashboard panel shell — the chart loader never becomes ready. */
function shouldSkipDemoEntryAnalysisLoader(
  previousPath: string,
  nextPath: string
): boolean {
  return (
    isDemoEntryAnalysisPathname(previousPath) || isDemoEntryAnalysisPathname(nextPath)
  );
}

function shouldSkipRouteLoader(previousPath: string | null, nextPath: string): boolean {
  if (!previousPath) return false;
  return (
    shouldSkipUniversityCourseLoader(previousPath, nextPath) ||
    shouldSkipNotificationsCentreLoader(previousPath, nextPath) ||
    shouldSkipAuthLoader(previousPath, nextPath) ||
    shouldSkipDemoEntryAnalysisLoader(previousPath, nextPath)
  );
}

export function RouteChangeLoader() {
  const location = useLocation();
  const navigationKey = useMemo(() => location.pathname, [location.pathname]);
  const isAccountSettingsRoute = location.pathname === "/profile";
  const { isLoaded: clerkAuthReady } = useAuth();
  const pageContentReady = usePageContentReady(navigationKey);
  const readyToDismiss = clerkAuthReady && pageContentReady;

  const previousRouteRef = useRef<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loaderRun, setLoaderRun] = useState(1);

  useLayoutEffect(() => {
    if (previousRouteRef.current === navigationKey) {
      return;
    }

    const previousRoute = previousRouteRef.current;
    previousRouteRef.current = navigationKey;

    // Initial mount / hard refresh: show the candle loader (auth screens stay clear).
    if (previousRoute === null) {
      if (isAuthPathname(navigationKey)) {
        return;
      }
      setIsVisible(true);
      setLoaderRun((value) => value + 1);
      return;
    }

    if (shouldSkipRouteLoader(previousRoute, navigationKey)) {
      // In-place inbox / auth / university transitions: do not start a new run.
      // Leave any loader already started by navigating into this area alone —
      // e.g. /dashboard → /notifications starts the chart, then the auto-select
      // replace to /notifications/:id must not abort it mid-flight.
      return;
    }

    setIsVisible(true);
    setLoaderRun((value) => value + 1);
  }, [navigationKey]);

  const handleComplete = useCallback(() => {
    setIsVisible(false);
  }, []);

  if (!isVisible || isAccountSettingsRoute) {
    return null;
  }

  return createPortal(
    <LoadingChart key={loaderRun} readyToDismiss={readyToDismiss} onComplete={handleComplete} />,
    document.body
  );
}
